import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { getScoreboard } from '../../api/runs';
import { ApiError } from '../../api/client';
import { useAuth } from '../../auth/useAuth';
import type { Scoreboard, ScoreboardEntry } from '../../api/types';
import { formatRelativeTime } from '../../utils/relativeTime';
import ErrorAlert from '../../components/ErrorAlert';
import LoadingSpinner from '../../components/LoadingSpinner';

const AUTO_REFRESH_MS = 10_000;

const ParticipantLeaderboard: React.FC = () => {
  const { t } = useTranslation('runs');
  const { user } = useAuth();
  const { runId } = useParams<{ runId: string }>();
  const orgSlug = user?.organization_slug ?? '';

  const ownTeamId = runId
    ? sessionStorage.getItem(`clavis_team_for_run_${runId}`)
    : null;

  const [scoreboard, setScoreboard] = useState<Scoreboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Date.now() can't be called during render (React Compiler purity rule) —
  // snapshot it alongside each load, which already re-renders every 10s.
  const [now, setNow] = useState<number>(() => Date.now());

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    if (!orgSlug || !runId) return;
    setError(null);
    try {
      const data = await getScoreboard(orgSlug, runId);
      setScoreboard(data);
      setNow(Date.now());
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setLoading(false);
    }
  }, [orgSlug, runId]);

  useEffect(() => {
    void load();
    intervalRef.current = setInterval(() => void load(), AUTO_REFRESH_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [load]);

  if (loading) return <LoadingSpinner />;

  const entries = scoreboard?.entries ?? [];

  return (
    <Box>
      <Typography variant="h2" component="h1" sx={{ mb: 2 }}>
        {t('participant.leaderboard.title')}
      </Typography>

      {error && <ErrorAlert message={error} onRetry={load} />}

      {entries.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <Avatar
              sx={{
                bgcolor: 'secondary.light',
                color: 'text.primary',
                width: 64,
                height: 64,
                mx: 'auto',
                mb: 2,
              }}
            >
              <EmojiEventsIcon fontSize="large" />
            </Avatar>
            <Typography variant="h5" component="p" gutterBottom>
              {t('participant.leaderboard.empty.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('participant.leaderboard.empty.subtitle')}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={1}>
          {entries.map((entry, idx) => (
            <LeaderboardRow
              key={entry.team_id}
              entry={entry}
              rank={idx + 1}
              isOwnTeam={entry.team_id === ownTeamId}
              now={now}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

interface RowProps {
  entry: ScoreboardEntry;
  rank: number;
  isOwnTeam: boolean;
  now: number;
}

const LeaderboardRow: React.FC<RowProps> = ({ entry, rank, isOwnTeam, now }) => {
  const { t } = useTranslation('runs');
  const isFirst = rank === 1;

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: isOwnTeam ? 'secondary.main' : undefined,
        bgcolor: isOwnTeam ? 'secondary.light' : 'background.paper',
        borderWidth: isOwnTeam ? 2 : 1,
      }}
    >
      <CardContent
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 1.5,
          '&:last-child': { pb: 1.5 },
        }}
      >
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: isFirst ? 'secondary.main' : 'primary.main',
            color: isFirst ? 'text.primary' : 'primary.contrastText',
            fontWeight: 700,
          }}
          aria-label={`Rank ${rank}`}
        >
          {isFirst ? <EmojiEventsIcon /> : rank}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" component="span" noWrap>
              {entry.team_name}
            </Typography>
            {isOwnTeam && (
              <Typography
                variant="caption"
                component="span"
                sx={{ color: 'text.primary', fontWeight: 600 }}
              >
                · {t('participant.leaderboard.yourTeam')}
              </Typography>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {entry.last_scored_at
              ? formatRelativeTime(entry.last_scored_at, now, t)
              : t('participant.leaderboard.neverScored')}
          </Typography>
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, ml: 'auto' }}>
          {t('participant.leaderboard.points', { points: entry.total_points })}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default ParticipantLeaderboard;
